import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import {
  Type,
  cssColorValue,
  spacer12,
  spacer48,
  spacer24,
  ButtonPrimary,
} from '@spotify-internal/encore-web-v5';
import { useTracking } from '../features/Tracking';
import { ButtonPrimaryAsLink } from '../features/ui';
import { defaultTheme } from '../features/Theme';
import { NotFound, PageHead } from '../features/Layout';
import { useT } from '@spotify-internal/i18n-core';
import { useCurrentUser } from '../@mrkt/useCurrentUser';
import { NextPageContext } from 'next';

const Layout = styled.div`
  display: flex;
  width: 100%;
  background-color: ${cssColorValue('essentialAnnouncement')};
  padding: 120px ${spacer24};
`;

const Content = styled.div`
  width: 100%;
  max-width: 800px;
  text-align: center;
  margin: auto;
  p {
    margin-top: ${spacer48};
    margin-bottom: ${spacer12};
  }
`;

const ContentfulError = () => {
  const t = useT();
  const { sendEvent } = useTracking();
  const user = useCurrentUser();
  const loggedIn = user?.hasAccess;

  React.useEffect(() => {
    sendEvent({
      eventCategory: `Frodor - Contentful failed to load`,
      eventAction: 'view',
      eventLabel: 'Contentful failure',
    });
  }, [sendEvent]);

  return (
    <ThemeProvider theme={defaultTheme} data-testid="contentful-error">
      <PageHead
        title={t('FRODOR_CONTENTFUL_ERROR_HEADER', 'Content not found', '')}
      />

      <Layout>
        <Content>
          <Type.h1 paddingBottom="0.67em" variant="forte">
            {t(
              'FRODOR_CONTENTFUL_ERROR_HEADING',
              'We couldn’t load this page.',
              "Couldn't means could not",
            )}
          </Type.h1>

          {loggedIn ? (
            <>
              <Type.p variant="ballad">
                <div>
                  {t(
                    'FRODOR_CONTENTFUL_ERROR_BODY_LOGGED_IN_DESCRIPTION',
                    'We’re working on getting things back online.',
                    'Text describing basic error',
                  )}
                </div>
                <div>
                  {t(
                    'FRODOR_CONTENTFUL_ERROR_BODY_LOGGED_IN_ADVICE',
                    'Meanwhile, you can go to your dashboard or check back later.',
                    'Advice on what to do since page is not working',
                  )}
                </div>
              </Type.p>
              <ButtonPrimaryAsLink href="/c" prefetch={false}>
                {t(
                  'FRODOR_CONTENTFUL_ERROR_CTA_LOGGED_IN',
                  'Go to dashboard',
                  '',
                )}
              </ButtonPrimaryAsLink>
            </>
          ) : (
            <>
              <Type.p variant="ballad">
                {t(
                  'FRODOR_CONTENTFUL_ERROR_BODY_LOGGED_OUT',
                  'We’re working on getting things back online. Meanwhile, you can try again or check back later.',
                  'Link here refers to a web link',
                )}
              </Type.p>
              <ButtonPrimary onClick={() => window.location.reload()}>
                {t('FRODOR_CONTENTFUL_ERROR_CTA_LOGGED_OUT', 'Retry', '')}
              </ButtonPrimary>
            </>
          )}
        </Content>
      </Layout>
    </ThemeProvider>
  );
};

export default function CustomError({ notFound = false }) {
  if (notFound) return <NotFound />;
  return <ContentfulError />;
}

CustomError.getInitialProps = ({ res, err }: NextPageContext) => {
  const notFound = err?.message === 'NOT_FOUND';

  if (notFound && res) {
    res.statusCode = 404;
  }

  return {
    notFound,
  };
};
